import { createRouter, createWebHistory } from "vue-router";
import { api, currentUser, ensureSession, setSession, token } from "@/lib/api";
import { loadAppearance } from "@/lib/appearanceRuntime";
import { loadReplyDisplay } from "@/lib/replyDisplay";
import { loadSidebarModules } from "@/lib/sidebarModules";
import { hasErpSignInError } from "@/lib/erpSignIn";
import { connectLive } from "@/lib/live";
import { beginPageView } from "@/lib/pageView";

/**
 * Every screen inside the Genie shell is one route and one URL. The shell is a
 * single component — the panels take over its centre while the composer and
 * the rail stay put — so all of these records point at the same component and
 * differ only in `meta.screen`, which the shell reads to decide what to show.
 * Vue Router keeps the component instance across such navigations, so moving
 * between screens does not remount the shell or drop the live socket.
 *
 * A chat is `/chat/:id`. That is what makes two browser windows independent:
 * each one carries its own thread in its address bar rather than both asking
 * the server for "the most recent one".
 */
const Genie = () => import("@/views/Genie.vue");

const router = createRouter({
  history: createWebHistory(),
  routes: [
    { path: "/", name: "home", component: Genie, meta: { screen: "home" } },
    { path: "/chat/:id", name: "chat", component: Genie, meta: { screen: "chat" } },
    { path: "/chat/:id/trace", name: "trace", component: Genie, meta: { screen: "trace" } },
    { path: "/chats", name: "chats", component: Genie, meta: { screen: "chats" } },
    { path: "/desk", name: "desk", component: Genie, meta: { screen: "desk" } },
    // The results panel's own front page: every document list on the site.
    { path: "/documents/:doctype?", name: "documents", component: Genie, meta: { screen: "documents" } },
    { path: "/templates", name: "gallery", component: Genie, meta: { screen: "gallery" } },
    { path: "/automations", name: "automations", component: Genie, meta: { screen: "automations" } },
    // The roles handed to Genie, and one of them open.
    { path: "/agents/builder/:blueprintId?", name: "agent-builder", component: Genie, meta: { screen: "agent-builder", admin: true } },
    { path: "/agents/about/:agentId", name: "agent-about", component: Genie, meta: { screen: "agents" } },
    { path: "/agents/activate/:agentId", name: "agent-activate", component: Genie, meta: { screen: "agents" } },
    { path: "/agents/:id/settings", name: "agent-settings", component: Genie, meta: { screen: "agents" } },
    { path: "/agents/:id?", name: "agents", component: Genie, meta: { screen: "agents" } },
    // Everything waiting on the signed-in person, across every doctype.
    { path: "/erp-operations", name: "erp-operations", component: Genie, meta: { screen: "erp-operations" } },
    { path: "/approvals", name: "approvals", component: Genie, meta: { screen: "approvals" } },
    { path: "/approvals/dashboard", name: "approvals-dashboard", component: Genie, meta: { screen: "approvals-dashboard" } },
    // Every module's dashboards, from the operator's own workspaces.
    { path: "/analytics/:module?", name: "analytics", component: Genie, meta: { screen: "analytics" } },
    // The operator's own reports and dashboards, stored on the site.
    { path: "/reports/:name?", name: "reports", component: Genie, meta: { screen: "reports" } },
    { path: "/dashboards/:name?", name: "dashboards", component: Genie, meta: { screen: "dashboards" } },
    { path: "/inbox", name: "inbox", component: Genie, meta: { screen: "inbox" } },
    { path: "/intake", name: "intake", component: Genie, meta: { screen: "intake" } },
    { path: "/knowledge", name: "knowledge", component: Genie, meta: { screen: "knowledge" } },
    // The playbooks, and one of them open on its own page.
    { path: "/skills/:slug?", name: "skills", component: Genie, meta: { screen: "skills" } },
    {
      path: "/operations",
      name: "operations",
      component: Genie,
      meta: { screen: "admin", admin: true },
    },
    // Every request's lifecycle, from question to model, tools and answer.
    {
      path: "/request-analytics",
      name: "request-analytics",
      component: Genie,
      meta: { screen: "request-analytics", admin: true },
    },
    // Every view the results panel can draw, each run live, on one screen.
    {
      path: "/views-gallery",
      name: "views-gallery",
      component: Genie,
      meta: { screen: "views-gallery", admin: true },
    },
    // Where Genie used to live, and a bare /chat. Kept so existing links and
    // bookmarks land somewhere sensible.
    { path: "/genie", redirect: "/" },
    { path: "/chat", redirect: "/chats" },
    // The classic console, still reachable by anyone who wants it or has it
    // bookmarked.
    { path: "/console", name: "console", component: () => import("@/views/Console.vue") },
    // The person's own screens, inside the shell: the rail stays put.
    { path: "/profile", name: "profile", component: Genie, meta: { screen: "profile" } },
    { path: "/appearance", name: "appearance", component: Genie, meta: { screen: "appearance" } },
    { path: "/agent", name: "agent", component: Genie, meta: { screen: "agent" } },
    {
      path: "/settings",
      name: "settings",
      component: () => import("@/views/Settings.vue"),
      meta: { admin: true },
    },
    { path: "/signin", name: "signin", component: () => import("@/views/SignIn.vue"), meta: { public: true } },
    { path: "/:pathMatch(.*)*", redirect: "/" },
  ],
});

router.beforeEach(async (to) => {
  // Already signed in: the sign-in screen has nothing to offer, so go where the
  // operator was heading. Except when it was sent there to explain an ERP
  // credential that lapsed: then it is the way to sign in to ERPNext again.
  // The guard below checks the session on the way in; one the server no longer
  // honours is cleared there and comes back here signed out.
  if (to.name === "signin" && token.value && !hasErpSignInError()) {
    return typeof to.query.next === "string" && /^\/[^/\\]/.test(to.query.next) ? to.query.next : "/";
  }
  if (to.meta.public) return true;

  // A new tab starts as the last operator who signed in here, on its own session.
  await ensureSession();

  // The address the operator was heading for rides along, so a shared
  // /chat/:id link opened while signed out still lands on that thread.
  if (!token.value) {
    return to.fullPath === "/" ? { name: "signin" } : { name: "signin", query: { next: to.fullPath } };
  }

  // Validate the stored token once per page load before rendering the console.
  if (!currentUser.value) {
    try {
      const { user } = await api.me();
      currentUser.value = user;
      // Their look, from wherever they last chose it. Not awaited: the
      // cached one is already on screen.
      void loadAppearance();
      // The site's reply-display house style, applied to everyone signed in.
      void loadReplyDisplay();
      // Which modules the site's rail lists.
      void loadSidebarModules();
      connectLive();
    } catch {
      setSession(null);
      return { name: "signin" };
    }
  }

  // Hiding admin routes from non-admins is courtesy, not security — the
  // routes they call are gated server-side, and typing the URL gets a 403
  // either way. Redirecting is simply better than rendering a screen of
  // failed requests.
  if (to.meta.admin && !currentUser.value?.isAdmin) return { name: "home" };

  return true;
});

// A page view for request analytics: the route's NAME only — never its query,
// a form's fields or anything typed. Through the api client, so an expired
// access token is refreshed rather than the event quietly lost.
router.afterEach((to, _from, failure) => {
  if (failure || to.meta.public || !token.value) return;
  // A new page load: every call from here on carries its id (lib/pageView.ts).
  beginPageView(String(to.name ?? "unknown"));
  const conversationId = to.name === "chat" && typeof to.params.id === "string" ? to.params.id : undefined;
  void api
    .recordActivity({ action: "page_view", screen: String(to.name ?? "unknown"), ...(conversationId ? { conversationId } : {}) })
    .catch(() => {});
});

export default router;
